const MAX_EXTRA_STRING_LENGTH = 500;

/**
 * Serialize a value caught by an error boundary into a plain object safe to
 * send to /api/errors/.
 *
 * Libraries do not always throw real Error instances. react-beautiful-dnd
 * throws a bare `{ message }` object with no name and no stack (logged as an
 * anonymous "Invariant failed", error id 82086), and `throw null` /
 * `throw 'string'` are legal JavaScript. Reading `.name` on those either
 * crashes the reporter or produces an empty record, so every access here is
 * guarded and the constructor name is included to identify the source even
 * when there is no stack.
 */
export function serializeCaughtError(thrown) {
    if (thrown == null) {
        return { message: `Caught throw of non-error value: ${String(thrown)}` };
    }
    if (typeof thrown !== 'object' && typeof thrown !== 'function') {
        return {
            message: String(thrown),
            constructorName: typeof thrown,
        };
    }
    const out = {
        name: thrown.name,
        message: thrown.message,
        stack: thrown.stack,
    };
    if (thrown.constructor && thrown.constructor.name) {
        out.constructorName = thrown.constructor.name;
    }
    const extra = {};
    Object.keys(thrown).forEach((key) => {
        if (key === 'name' || key === 'message' || key === 'stack') {
            return;
        }
        const value = thrown[key];
        if (value == null) {
            return;
        }
        const type = typeof value;
        if (type === 'string') {
            extra[key] = value.length > MAX_EXTRA_STRING_LENGTH
                ? value.slice(0, MAX_EXTRA_STRING_LENGTH) : value;
        } else if (type === 'number' || type === 'boolean') {
            extra[key] = value;
        }
    });
    if (Object.keys(extra).length > 0) {
        out.extra = extra;
    }
    return out;
}

/**
 * List the drawers currently open, as 'componentName@level' strings.
 * The hash route alone does not tell WHICH form was on screen when a crash
 * happened — most editing happens in drawers that never change the route.
 */
export function getOpenDrawerComponents(state) {
    if (!state) {
        return [];
    }
    const open = [];
    for (let i = 1; i <= 9; i += 1) {
        if (state[`showDrawer${i}`] && state[`drawerComponent${i}`]) {
            open.push(`${state[`drawerComponent${i}`]}@${i}`);
        }
    }
    if (state.showTopSheet && state.topSheetComponent) {
        open.push(`${state.topSheetComponent}@topSheet`);
    }
    return open;
}
